import Vue from 'vue';
import Vuex from 'vuex';
import createPersistedState from 'vuex-persistedstate';
import { filterMenuByName } from '../utils/filterMenu';
import menuConfig from './menu';

Vue.use(Vuex);

const state = {
  sidebarShow: 'responsive',
  sidebarMinimize: false,
  asideShow: false,
  darkMode: false,
  roles: [],
  roleModulePermissions: [],
  roleSubModulePermissions: [],
  menu: [],
  isAuthenticated: false,
  isSuperAdmin: false,
  currentUser: null,
};

const mutations = {
  toggleSidebarDesktop(state) {
    const sidebarOpened = [true, 'responsive'].includes(state.sidebarShow);
    state.sidebarShow = sidebarOpened ? false : 'responsive';
  },
  toggleSidebarMobile(state) {
    const sidebarClosed = [false, 'responsive'].includes(state.sidebarShow);
    state.sidebarShow = sidebarClosed ? true : 'responsive';
  },
  set(state, [variable, value]) {
    state[variable] = value;
  },
  toggle(state, variable) {
    state[variable] = !state[variable];
  },
  setRoles(state, roles) {
    state.roles = roles;
  },
  setRoleModulePermissions(state, permissions) {
    state.roleModulePermissions = permissions;
  },
  setRoleSubModulePermissions(state, permissions) {
    state.roleSubModulePermissions = permissions;
  },
  setMenu(state, menu) {
    state.menu = menu;
  },
  setAuthenticated(state, isAuthenticated) {
    state.isAuthenticated = isAuthenticated;
  },
  setIsSuperAdmin(state, isSuperAdmin) {
    state.isSuperAdmin = isSuperAdmin;
  },
  setCurrentUser(state, user) {
    state.currentUser = user;
  },
};

const actions = {
  login({ commit, dispatch }, { roles, roleModulePermissions, roleSubModulePermissions, isSuperAdmin, user }) {
    commit('setAuthenticated', true);
    commit('setRoles', roles || []);
    commit('setRoleModulePermissions', roleModulePermissions || []);
    commit('setRoleSubModulePermissions', roleSubModulePermissions || []);
    commit('setIsSuperAdmin', isSuperAdmin || false);
    commit('setCurrentUser', user || null);
    // Update the menu after login
    dispatch('updateMenu');
  },
  logout({ commit }) {
    commit('setAuthenticated', false);
    commit('setRoles', []);
    commit('setRoleModulePermissions', []);
    commit('setRoleSubModulePermissions', []);
    commit('setMenu', []);
    commit('setIsSuperAdmin', false);
    commit('setCurrentUser', null);
  },
  updateMenu({ commit, state }) {
    // Filter menu based on roleModulePermissions, roleSubModulePermissions, and isSuperAdmin
    const filteredMenu = filterMenuByName(
      menuConfig,
      state.roleModulePermissions,
      state.roleSubModulePermissions,
      state.isSuperAdmin
    );
    commit('setMenu', filteredMenu);
  },
};

const getters = {
  roles: state => state.roles,
  roleModulePermissions: state => state.roleModulePermissions,
  roleSubModulePermissions: state => state.roleSubModulePermissions,
  menu: state => state.menu,
  isAuthenticated: state => state.isAuthenticated,
  isSuperAdmin: state => state.isSuperAdmin,
  currentUser: state => state.currentUser,

  /**
   * Check if user has permission for a specific module
   * @param {string} moduleCode - Module code (e.g., 'STOCKS', 'CUSTOMER')
   * @param {string} permissionType - Permission type ('CanView', 'CanAdd', 'CanUpdate', 'CanDelete')
   * @returns {boolean}
   */
  hasModulePermission: (state) => (moduleCode, permissionType = 'CanView') => {
    if (state.isSuperAdmin) return true;

    const permission = state.roleModulePermissions.find(
      p => {
        const module = p.Module || p.module;
        const code = module?.Code || module?.code;
        return code === moduleCode;
      }
    );

    if (!permission) return false;

    // Handle both PascalCase and camelCase
    return permission[permissionType] === true || permission[permissionType.charAt(0).toLowerCase() + permissionType.slice(1)] === true;
  },

  /**
   * Check if user has permission for a specific submodule
   * @param {string} subModuleCode - Submodule code (e.g., 'STOCK_INFO', 'VEHICLE_INFO')
   * @param {string} permissionType - Permission type ('CanView', 'CanAdd', 'CanUpdate', 'CanDelete')
   * @returns {boolean}
   */
  hasSubModulePermission: (state) => (subModuleCode, permissionType = 'CanView') => {
    if (state.isSuperAdmin) return true;

    console.log('=== hasSubModulePermission ===');
    console.log('Looking for subModuleCode:', subModuleCode);
    console.log('roleSubModulePermissions:', state.roleSubModulePermissions);

    const permission = state.roleSubModulePermissions.find(
      p => {
        const subModule = p.SubModule || p.subModule;
        const code = subModule?.Code || subModule?.code;
        console.log(`  Checking submodule code: ${code} === ${subModuleCode}? ${code === subModuleCode}`);
        return code === subModuleCode;
      }
    );

    console.log('Found permission:', permission);

    if (!permission) {
      console.log(`No permission found for ${subModuleCode}`);
      return false;
    }

    // Handle both PascalCase and camelCase
    const hasPermission = permission[permissionType] === true || permission[permissionType.charAt(0).toLowerCase() + permissionType.slice(1)] === true;
    console.log(`Permission ${permissionType} for ${subModuleCode}:`, hasPermission);
    return hasPermission;
  },

  /**
   * Get module permission object
   * @param {string} moduleCode - Module code
   * @returns {object|null}
   */
  getModulePermission: (state) => (moduleCode) => {
    if (state.isSuperAdmin) {
      return {
        CanView: true,
        CanAdd: true,
        CanUpdate: true,
        CanDelete: true
      };
    }

    return state.roleModulePermissions.find(
      p => p.Module && p.Module.Code === moduleCode
    );
  },

  /**
   * Get submodule permission object
   * @param {string} subModuleCode - Submodule code
   * @returns {object|null}
   */
  getSubModulePermission: (state) => (subModuleCode) => {
    if (state.isSuperAdmin) {
      return {
        CanView: true,
        CanAdd: true,
        CanUpdate: true,
        CanDelete: true
      };
    }

    return state.roleSubModulePermissions.find(
      p => p.SubModule && p.SubModule.Code === subModuleCode
    );
  },

  /**
   * Check if user can access a specific route based on module/submodule
   * @param {string} moduleCode - Module code
   * @param {string} subModuleCode - Optional submodule code
   * @returns {boolean}
   */
  canAccessRoute: (state, getters) => (moduleCode, subModuleCode = null) => {
    if (state.isSuperAdmin) return true;

    // Check module permission first
    if (!getters.hasModulePermission(moduleCode, 'CanView')) {
      return false;
    }

    // If submodule specified, check submodule permission
    if (subModuleCode) {
      return getters.hasSubModulePermission(subModuleCode, 'CanView');
    }

    return true;
  },

  /**
   * Get all accessible modules for current user
   * @returns {array}
   */
  accessibleModules: (state) => {
    if (state.isSuperAdmin) {
      return state.roleModulePermissions.map(p => p.Module?.Code).filter(Boolean);
    }

    return state.roleModulePermissions
      .filter(p => p.CanView)
      .map(p => p.Module?.Code)
      .filter(Boolean);
  },

  /**
   * Get all accessible submodules for current user
   * @returns {array}
   */
  accessibleSubModules: (state) => {
    if (state.isSuperAdmin) {
      return state.roleSubModulePermissions.map(p => p.SubModule?.Code).filter(Boolean);
    }

    return state.roleSubModulePermissions
      .filter(p => p.CanView)
      .map(p => p.SubModule?.Code)
      .filter(Boolean);
  },
};

export default new Vuex.Store({
  state,
  mutations,
  actions,
  getters,
  plugins: [
    createPersistedState({
      // Only persist UI preferences, not auth/permission data
      // Auth and permissions are refreshed on login via recordLogin()
      paths: ['sidebarShow', 'sidebarMinimize', 'asideShow', 'darkMode']
    })
  ],
});
